import { useId, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { mensajeDeCampo, tieneCodigo } from '../api';
import type { Rol } from '../api';
import { altaDe, crearUsuario } from './api';
import { useRefrescarUsuarios } from './consultas';
import { mensajeDeUsuarios } from './mensajes';

/**
 * Alta de una persona (KAN-143).
 *
 * # EL ROL ARRANCA EN OPERADOR
 *
 * No es una preferencia de orden en un `<select>`: es la misma decisión que el
 * backend tomó al hacer que un alta sin `rol` cree un `OPERADOR`. **El default es
 * el menor privilegio**, para que la distracción de quien completa el formulario
 * no fabrique un administrador. Preseleccionar ADMIN acá anularía esa decisión
 * desde la única pantalla que la gente va a usar.
 *
 * # LOS ERRORES VAN DONDE ESTÁ LA CAUSA
 *
 * Un `400 request_invalida` trae un mapa `campos` con el error por campo: se
 * muestra pegado al campo, no como un cartel arriba que obliga a adivinar cuál de
 * los cuatro está mal. El `409 ya_existe` (email repetido) sí es del formulario
 * entero, pero se muestra además al lado del email, que es el campo que lo causó.
 */
export function FormularioDeAlta() {
  const idNombre = useId();
  const idEmail = useId();
  const idPassword = useId();
  const idRol = useId();

  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rol, setRol] = useState<Rol>('OPERADOR');

  const refrescar = useRefrescarUsuarios();

  const alta = useMutation({
    mutationFn: () => crearUsuario(altaDe({ nombre, email, password, rol })),
    onSuccess: () => {
      setNombre('');
      setEmail('');
      setPassword('');
      setRol('OPERADOR'); // Vuelve al menor privilegio, no al último elegido.
      refrescar();
    },
  });

  const errorDeNombre = mensajeDeCampo(alta.error, 'nombre');
  // El 409 de email repetido se marca también en el campo que lo causó: se
  // decide por el CÓDIGO (`tieneCodigo`), nunca leyendo el `detalle`.
  const errorDeEmail =
    mensajeDeCampo(alta.error, 'email') ??
    (tieneCodigo(alta.error, 'ya_existe') ? 'Ya está en uso.' : null);
  const errorDePassword = mensajeDeCampo(alta.error, 'password');

  return (
    <form
      onSubmit={(evento) => {
        evento.preventDefault();
        alta.mutate();
      }}
      noValidate
    >
      <h2>Dar de alta una persona</h2>
      <p>
        <label htmlFor={idNombre}>Nombre</label>
        <br />
        <input
          id={idNombre}
          name="nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          aria-invalid={errorDeNombre !== null}
          aria-describedby={errorDeNombre ? `${idNombre}-error` : undefined}
          required
        />
        {errorDeNombre ? (
          <span id={`${idNombre}-error`} className="error">
            {' '}
            {errorDeNombre}
          </span>
        ) : null}
      </p>
      <p>
        <label htmlFor={idEmail}>Email</label>
        <br />
        <input
          id={idEmail}
          name="email"
          type="email"
          autoComplete="off"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-invalid={errorDeEmail !== null}
          aria-describedby={errorDeEmail ? `${idEmail}-error` : undefined}
          required
        />
        {errorDeEmail ? (
          <span id={`${idEmail}-error`} className="error">
            {' '}
            {errorDeEmail}
          </span>
        ) : null}
      </p>
      <p>
        <label htmlFor={idPassword}>Contraseña</label>
        <br />
        <input
          id={idPassword}
          name="password"
          type="password"
          // Es la contraseña de OTRA persona: que el navegador no la ofrezca
          // guardar como si fuera la de quien está usando la pantalla.
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          aria-invalid={errorDePassword !== null}
          aria-describedby={errorDePassword ? `${idPassword}-error` : undefined}
          required
        />
        {errorDePassword ? (
          <span id={`${idPassword}-error`} className="error">
            {' '}
            {errorDePassword}
          </span>
        ) : null}
        <br />
        <span className="nota">Mínimo 12 caracteres. La persona la puede cambiar después.</span>
      </p>
      <p>
        <label htmlFor={idRol}>Rol</label>
        <br />
        <select id={idRol} name="rol" value={rol} onChange={(e) => setRol(e.target.value as Rol)}>
          {/* OPERADOR primero Y seleccionado: el default es el menor privilegio. */}
          <option value="OPERADOR">OPERADOR</option>
          <option value="ADMIN">ADMIN</option>
        </select>
        <br />
        <span className="nota">
          Un OPERADOR emite comprobantes. Un ADMIN además administra usuarios y sistemas.
        </span>
      </p>
      {alta.isError ? (
        <p role="alert" className="error">
          {mensajeDeUsuarios(alta.error, 'alta')}
        </p>
      ) : null}
      {alta.isSuccess ? (
        <p role="status">Se dio de alta a {alta.data.nombre}.</p>
      ) : null}
      <button type="submit" disabled={alta.isPending}>
        {alta.isPending ? 'Dando de alta…' : 'Dar de alta'}
      </button>
    </form>
  );
}
